/**
 * When work is due, in words — split out of `work.ts` so the header does not carry the rest.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS ITS OWN FILE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `App.tsx` imports `components/lumen/MyWork.tsx` EAGERLY — it sits in the header on every
 * screen — and `MyWork` wanted one function from `work.ts`. That pulled the whole of the Work
 * module's rules into the ENTRY chunk: the status and priority tables, the starter checklists,
 * the alarm prefill, the event wording. All of it paid on every cold load, by everyone, before
 * the sign-in screen, for one sentence about a due date.
 *
 * `scripts/bundle-budget.mjs` exists because that is exactly how a bundle grows — "nobody adds
 * half a megabyte; they add one component that imports one library". So the two or three
 * functions the header genuinely needs live here, and `work.ts` goes back to being part of the
 * Work chunk.
 *
 * Pure, and tested in `work-dates.test.ts`.
 */
import type { WorkOrder } from '@/lib/api/work';

const DAY = 86_400_000;

function localMidnight(at: number): number {
  const d = new Date(at);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * "Due today", "Due in 3 days", "Overdue by 2 days", "No date": how far, in local days.
 *
 * Reads `open` rather than asking whether the status is one of two words (0105): the status is
 * the organisation's to name, and the API already says whether the work is outstanding.
 */
export function dueText(w: Pick<WorkOrder, 'dueAt' | 'open' | 'overdue'>, now: number): string {
  if (w.dueAt === null) return 'No date';
  const days = Math.round((localMidnight(Date.parse(w.dueAt)) - localMidnight(now)) / DAY);
  if (w.open && w.overdue) return days >= 0 ? 'Overdue' : `Overdue by ${-days} ${-days === 1 ? 'day' : 'days'}`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days > 1) return `Due in ${days} days`;
  return `Was due ${-days} ${-days === 1 ? 'day' : 'days'} ago`;
}

/** A date input's `YYYY-MM-DD` as the END of that local day — what "due on the 12th" means. */
export function endOfLocalDay(day: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 0);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** An instant as the local `YYYY-MM-DD` a date input shows. */
export function localDay(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "2 h 15 m", "3 d 4 h", "—": a duration in the console's shorthand. */
export function durationText(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/**
 * `26/Oct/2026` — the FESCO design's due date (§32), on the board's table and its cards.
 *
 * Short on purpose. `dueText` says "Overdue by 6 days", which is the right thing in a drawer and
 * the wrong thing in a 96px column: it wrapped to two lines on every overdue row, which is most
 * of them on a busy board. The row still marks overdue in red — the word is simply not repeated
 * sixty times down one column.
 */
export function dueDate(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en', { month: 'short' });
  return `${String(d.getDate()).padStart(2, '0')}/${month}/${d.getFullYear()}`;
}
